// frontend/src/contexts/BrandingContext.jsx
// #34 - applies a tenant's custom branding (colors, font, logo, custom CSS,
// white-label) app-wide once the user is authenticated. Reads from the
// lightweight GET /tenants/branding endpoint (available to any tenant
// member, not just admins) so every logged-in user sees the tenant's look,
// not just tenant admins editing settings.
import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useAuth } from './AuthContext'
import tenantSettingsService from '../services/tenantSettings'
import { BRANDING_CSS_VAR_MAP, fontFamilyCssValue, googleFontsHref, isSystemFont } from '../lib/branding'

const BrandingContext = createContext({ branding: null, loading: true })

const CUSTOM_CSS_STYLE_ID = 'tenant-custom-css'
const GOOGLE_FONT_LINK_ID = 'tenant-branding-font-link'

// Exported so TenantBrandingPanel can apply/revert an unsaved draft live
// across the app as a "preview before applying" (#34), without needing a
// round-trip to the backend.
export function applyBrandingCssVariables(branding) {
  const root = document.documentElement.style

  for (const [field, cssVars] of Object.entries(BRANDING_CSS_VAR_MAP)) {
    const value = branding?.[field]
    for (const cssVar of cssVars) {
      if (value) {
        root.setProperty(cssVar, value)
      } else {
        root.removeProperty(cssVar)
      }
    }
  }

  if (branding?.fontFamily) {
    root.setProperty('--font-family-base', fontFamilyCssValue(branding.fontFamily))
    if (isSystemFont(branding.fontFamily)) {
      // Switching FROM a Google font TO a curated system font must drop the
      // stale <link>, or the old font keeps loading over the network.
      document.getElementById(GOOGLE_FONT_LINK_ID)?.remove()
    } else {
      let link = document.getElementById(GOOGLE_FONT_LINK_ID)
      if (!link) {
        link = document.createElement('link')
        link.id = GOOGLE_FONT_LINK_ID
        link.rel = 'stylesheet'
        document.head.appendChild(link)
      }
      link.href = googleFontsHref(branding.fontFamily)
    }
  } else {
    root.removeProperty('--font-family-base')
    document.getElementById(GOOGLE_FONT_LINK_ID)?.remove()
  }

  let styleTag = document.getElementById(CUSTOM_CSS_STYLE_ID)
  if (branding?.customCss) {
    if (!styleTag) {
      styleTag = document.createElement('style')
      styleTag.id = CUSTOM_CSS_STYLE_ID
      document.head.appendChild(styleTag)
    }
    styleTag.textContent = branding.customCss
  } else {
    styleTag?.remove()
  }
}

export function clearBrandingCssVariables() {
  const root = document.documentElement.style
  for (const cssVars of Object.values(BRANDING_CSS_VAR_MAP)) {
    cssVars.forEach((cssVar) => root.removeProperty(cssVar))
  }
  root.removeProperty('--font-family-base')
  document.getElementById(GOOGLE_FONT_LINK_ID)?.remove()
  document.getElementById(CUSTOM_CSS_STYLE_ID)?.remove()
}

const DEFAULT_TITLE = 'CoFormIt'

function applyDocumentTitle(branding, tenantName) {
  document.title = branding?.hideBranding && tenantName ? tenantName : DEFAULT_TITLE
}

export const BrandingProvider = ({ children }) => {
  const { user } = useAuth()
  const [branding, setBranding] = useState(null)
  const [tenantName, setTenantName] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Unsaved draft applied app-wide as a live "preview before applying" (#34).
  // Kept as context state (not applied imperatively by the settings panel) so
  // every consumer - including Navbar's logo/white-label rendering - reflects
  // it, and so it has exactly one lifecycle to reason about: set on Preview,
  // cleared on successful save / explicit revert / panel unmount.
  const [previewBranding, setPreviewBranding] = useState(null)
  const tenantId = user?.activeTenantId ?? user?.tenantId ?? null

  const refresh = useCallback(async () => {
    if (!tenantId) {
      setBranding(null)
      setTenantName(null)
      setLoading(false)
      setError(null)
      return null
    }
    setLoading(true)
    try {
      const data = await tenantSettingsService.getBranding()
      setBranding(data?.branding || null)
      setTenantName(data?.tenantName || null)
      setError(null)
      return null
    } catch (err) {
      console.error('Error loading tenant branding:', err)
      // Keep the last-known-good branding rather than un-branding the app on
      // a transient failure; surface the error so a caller (e.g. the settings
      // page) can tell the admin instead of failing silently.
      setError(err)
      return err
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    const effective = previewBranding ?? branding
    if (effective) {
      applyBrandingCssVariables(effective)
    } else {
      clearBrandingCssVariables()
    }
    applyDocumentTitle(effective, tenantName)
  }, [branding, previewBranding, tenantName])

  // Clear on unmount of the whole app (e.g. logout unmounting the provider tree).
  useEffect(() => () => {
    clearBrandingCssVariables()
    document.title = DEFAULT_TITLE
  }, [])

  const preview = useCallback((draftBranding) => {
    setPreviewBranding(draftBranding)
  }, [])

  // Zero-cost (no network) - just drops the draft overlay so the effect above
  // falls back to the last-saved `branding`.
  const clearPreview = useCallback(() => {
    setPreviewBranding(null)
  }, [])

  const value = {
    branding: previewBranding ?? branding,
    tenantName,
    loading,
    error,
    refresh,
    preview,
    clearPreview,
    previewing: previewBranding !== null,
  }

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  )
}

export const useBranding = () => useContext(BrandingContext)
